import { useState } from 'react';
import { Paper, Stack } from '@mui/material';

import ConfirmationModal from '../../shared/modal/ConfirmationModal';
import GhostButton from '../../shared/button/GhostButton';
import PrimaryButton from '../../shared/button/PrimaryButton';
import { BodyText } from '../../shared/typography/PrimaryTypography';

type BulkAction = 'approve' | 'delete' | 'confirm';

/** Consequence-explaining dialog copy per bulk action. */
const BULK_ACTION_DIALOGS: Record<
  BulkAction,
  { title: string; description: (count: number) => string; confirmLabel: string }
> = {
  approve: {
    title: 'Approve selected records?',
    description: (count) =>
      `Approve ${count} selected record${count === 1 ? '' : 's'} to go live. Records without a mapped metric will be skipped.`,
    confirmLabel: 'Approve'
  },
  delete: {
    title: 'Delete selected records?',
    description: (count) =>
      `Soft-delete ${count} selected record${count === 1 ? '' : 's'}. Already-approved records will be skipped.`,
    confirmLabel: 'Delete'
  },
  confirm: {
    title: 'Confirm the AI-suggested mappings?',
    description: (count) =>
      `Confirm the AI-suggested metric on each of the ${count} selected record${count === 1 ? '' : 's'}. Confirmed labels map automatically for this client on future uploads; records without an AI-suggested mapping are skipped.`,
    confirmLabel: 'Confirm'
  }
};

interface BulkActionBarProps {
  /** Number of currently selected records. */
  selectedCount: number;
  /** Whether a bulk request is in flight (disables both actions). */
  busy: boolean;
  /** Executes the bulk approval of the current selection. */
  onApproveSelected: () => Promise<void>;
  /** Executes the bulk soft-delete of the current selection. */
  onDeleteSelected: () => Promise<void>;
  /** Bulk-confirms the AI-suggested mappings in the selection (skips the rest). */
  onConfirmMappingsSelected: () => Promise<void>;
  /**
   * Opens the bulk-edit dialog (Maxine R5 ②) — the dialog is its own
   * consequence-explaining confirmation, so no ConfirmationModal here.
   */
  onEditSelected: () => void;
  /** Clears the current selection. */
  onClearSelection: () => void;
}

/**
 * Toolbar shown while records are selected: bulk approve / bulk delete with a
 * confirmation dialog, plus a clear-selection action. The parent owns the API
 * calls and the result summary.
 * @returns {JSX.Element}
 */
export default function BulkActionBar({
  selectedCount,
  busy,
  onApproveSelected,
  onDeleteSelected,
  onConfirmMappingsSelected,
  onEditSelected,
  onClearSelection
}: BulkActionBarProps) {
  const [pendingAction, setPendingAction] = useState<BulkAction | null>(null);

  const handleConfirm = async (): Promise<void> => {
    if (pendingAction === 'approve') {
      await onApproveSelected();
    } else if (pendingAction === 'delete') {
      await onDeleteSelected();
    } else if (pendingAction === 'confirm') {
      await onConfirmMappingsSelected();
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <BodyText weight="Medium" sx={{ flex: 1 }}>
          {selectedCount} record{selectedCount === 1 ? '' : 's'} selected
        </BodyText>
        <GhostButton dark onClick={onClearSelection} disabled={busy}>
          Clear selection
        </GhostButton>
        <GhostButton dark outlined onClick={onEditSelected} disabled={busy}>
          Edit selected
        </GhostButton>
        <GhostButton dark outlined onClick={() => setPendingAction('delete')} disabled={busy}>
          Delete selected
        </GhostButton>
        <GhostButton dark outlined onClick={() => setPendingAction('confirm')} disabled={busy}>
          Confirm mappings
        </GhostButton>
        <PrimaryButton onClick={() => setPendingAction('approve')} disabled={busy}>
          Approve selected
        </PrimaryButton>
      </Stack>
      <ConfirmationModal
        open={pendingAction !== null}
        title={pendingAction === null ? '' : BULK_ACTION_DIALOGS[pendingAction].title}
        description={
          pendingAction === null
            ? ''
            : BULK_ACTION_DIALOGS[pendingAction].description(selectedCount)
        }
        confirm_button_text={
          pendingAction === null ? '' : BULK_ACTION_DIALOGS[pendingAction].confirmLabel
        }
        confirm_loading={busy}
        handleCloseModal={() => setPendingAction(null)}
        // Returning the promise lets the modal await completion before closing.
        handleConfirm={handleConfirm}
      />
    </Paper>
  );
}
